import { FormControl, InputLabel, Select, MenuItem, CircularProgress, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ServiceObject } from '../../../@types/service.type';
import { NodeAttributeType, FaultCategory } from '../../../configs/enums';
import { useGetFilteredNodeAttributesByCustomRange } from '../../../queries/structure-query';

/**
 * The shape this field reads off a node-attribute row. Declared locally and
 * structurally rather than pulled from a wider type, so the field depends only
 * on the handful of properties it actually touches.
 */
interface NodeAttribute {
  id: number;
  propertyTypeCode?: string;
  value?: {
    name?: string;
    customerId?: string;
    customerId2?: string;
    categoryId?: number;
  };
}

interface FaultOption {
  label: string;
  value: string;
}

interface InteractionFaultSelectionFieldProps {
  service: ServiceObject;
  value: string;
  onChange: (value: string) => void;
  isRequired: boolean;
  /**
   * The template's own field title. Defaults to "Fault Selection" — the label was
   * hardcoded, so a Solution Action field was mislabelled as a fault.
   */
  title?: string;
}

export const InteractionFaultSelectionField: React.FC<InteractionFaultSelectionFieldProps> = ({
  service,
  value,
  onChange,
  isRequired,
  title,
}) => {
  const [options, setOptions] = useState<FaultOption[]>([]);

  const nodeAttributesQuery = useGetFilteredNodeAttributesByCustomRange(
    `${service?.contractId || service?.clientId}`,
    [NodeAttributeType.FaultCategory, NodeAttributeType.FaultCode],
    service.serviceTypeCode,
    FaultCategory.Observed,
    service.actionTypeCode
  );

  useEffect(() => {
    if (service.mainLocationNodeId && service.serviceTypeCode) {
      nodeAttributesQuery.refetch();
    }
    // Keyed on *every* input the query is built from, not just the two the guard reads:
    // contractId/clientId and actionTypeCode are part of the cache key, so a change in
    // either selects a different, never-fetched entry (the query is `enabled: false`)
    // and the field would sit empty until something else happened to re-run this.
    // `nodeAttributesQuery` is omitted deliberately: React Query returns a new object
    // every render, so including it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    service.mainLocationNodeId,
    service.serviceTypeCode,
    service.contractId,
    service.clientId,
    service.actionTypeCode,
  ]);

  useEffect(() => {
    if (nodeAttributesQuery.data) {
      const faultAttributes = nodeAttributesQuery.data.filter(
        (obj: NodeAttribute) => obj.propertyTypeCode === NodeAttributeType.FaultCode
      );
      const faults: FaultOption[] = faultAttributes.map((obj: NodeAttribute) => ({
        label: obj?.value?.name || '_',
        value: JSON.stringify({
          id: obj.id,
          name: obj.value?.name,
          customerId: obj.value?.customerId,
          customerId2: obj.value?.customerId2,
          categoryId: obj.value?.categoryId,
        }),
      }));
      setOptions(faults);
    }
  }, [nodeAttributesQuery.data]);

  if (nodeAttributesQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // labelId/id pair them up, so the label is programmatically associated with the
  // control rather than just sitting next to it.
  const labelId = `fault-selection-${service.id}-label`;
  const selectId = `fault-selection-${service.id}`;
  const label = `${title || 'Fault Selection'}${isRequired ? ' *' : ''}`;

  return (
    <FormControl fullWidth size="small">
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        value={value || ''}
        label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt, index) => (
          <MenuItem key={index} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
